// Import necessary react hooks
import { useEffect, useState } from "react";
// Import necessary react router hooks
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
// Import axios utility functions
import axios from "axios";
// Import necessary Material-UI components
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";

import {
  apiURI,
  authErrorRedirect,
  Loader,
  TableRowDetails,
  useAuthInfo,
} from "../common";
import { DeleteUserConfirmation } from "./";

export function UserDetails() {
  // Use the useParams hook to get the email parameter from the URL
  const { email } = useParams();
  // Use the useNavigate hook to programmatically navigate
  const history = useNavigate();
  // Use the useAuthInfo custom hook to get the decoded authentication information
  const decoded = useAuthInfo();
  // State to manage errors
  const [errors, setErrors] = useState();
  // State to manage the user data
  const [data, setData] = useState();
  // State to control the visibility of the delete user confirmation dialog
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  // Function to open the delete user confirmation dialog
  const handleConfirmationOpen = () => {
    setOpenConfirmationDialog(true);
  };
  // Function to close the delete user confirmation dialog
  const handleConfirmationClose = () => {
    setOpenConfirmationDialog(false);
  };
  // Use the useEffect hook to fetch user details when the component mounts or the email changes
  useEffect(() => {
    axios
      .get(`${apiURI}/auth/${email}`)
      .then((response) => {
        setData(response.data);
      })
      .catch((response) => {
        console.log("error in axios form call react", response.response.data);
        setErrors({});
        setErrors(response.response.data);
        authErrorRedirect(response.response.data, history);
      });

    // eslint-disable-next-line no-console
  }, [email]);
  // Log errors to the console
  errors && console.log(errors);

  return (
    <Grid
      container
      justifyContent="center"
      rowSpacing={2}
      columnSpacing={2}
      columns={12}
      marginTop="100px"
      sx={{
        marginLeft: "auto",
        marginRight: "auto",
        paddingBottom: 2,
        paddingRight: 2,
        maxWidth: "100vw",
        marginTop: "20px",
      }}
    >
      {/* Grid item for the user details card */}
      <Grid item xs={12} lg={4}>
        <Card
          sx={{
            borderRadius: "15px",
            px: 4,
            background:
              "linear-gradient(white, white) padding-box, radial-gradient(circle,  rgba(199,171,121,1) 50%, rgba(255,255,255,1) 150%) border-box",
            borderTop: "10px solid transparent",
            boxShadow: "0 8px 40px -12px rgba(0,0,0,0.4)",
            "&:hover": {
              boxShadow: "0 16px 55px -12.125px rgba(0,0,0,0.4)",
            },
          }}
        >
          <CardContent>
            {/* Typography component for the title of the card */}
            <Typography
              sx={{
                mb: "45px",
                mt: "25px",
              }}
              variant="h3"
              component="div"
              color="text.secondary"
            >
              User Details
            </Typography>
            {/* Conditional rendering based on the availability of user data */}
            {data ? (
              <TableRowDetails data={data} />
            ) : (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignContent: "center",
                  marginBottom: "50px",
                }}
              >
                {/*  If data is not available, render a loader */}
                <Loader />
              </div>
            )}
            {/*  Conditional rendering based on the user's role */}
            {decoded && decoded.role === "admin" && (
              <Grid
                container
                sx={{ px: 2, mt: 6, justifyContent: "space-between" }}
              >
                <Button
                  variant="contained"
                  onClick={() => history(`/editUser/${data.email}`)}
                >
                  Edit User
                </Button>
                <Button
                  variant="contained"
                  onClick={handleConfirmationOpen}
                  color="error"
                >
                  Delete User
                </Button>
              </Grid>
            )}
          </CardContent>
        </Card>
      </Grid>
      {/* Render the DeleteUserConfirmation component for confirming user deletion */}
      <DeleteUserConfirmation
        email={email}
        openConfirmationDialog={openConfirmationDialog}
        handleConfirmationClose={handleConfirmationClose}
      />
    </Grid>
  );
}
